import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import i18n, { SUPPORTED_LANGUAGES } from '../i18n';
import { ApiError } from '../lib/api';
import { signOut } from '../lib/auth';
import { fetchMe, type Me, updateMe } from '../lib/me';
import { StoryProgress } from '../components/StoryProgress';
import './Profile.css';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; me: Me }
  | { kind: 'unauthenticated' }
  | { kind: 'error'; message: string };

export function Profile() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (!cancelled) setState({ kind: 'ready', me });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setState({ kind: 'unauthenticated' });
          return;
        }
        const message = err instanceof Error ? err.message : t('common.unknownError');
        setState({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <div className="profile-body">
      <h1>{t('profile.heading')}</h1>
      <Body state={state} onMeUpdate={(me) => setState({ kind: 'ready', me })} />
      <StoryProgress />
    </div>
  );
}

function Body({
  state,
  onMeUpdate,
}: {
  state: State;
  onMeUpdate: (me: Me) => void;
}) {
  const { t } = useTranslation();
  switch (state.kind) {
    case 'loading':
      return <p className="profile-status">{t('common.loading')}</p>;
    case 'unauthenticated':
      return (
        <p className="profile-status">
          <Trans i18nKey="common.notSignedIn" components={[<a href="/" key="0" />]} />
        </p>
      );
    case 'error':
      return (
        <p className="profile-status profile-error">
          {t('common.error', { message: state.message })}
        </p>
      );
    case 'ready':
      return <ProfileBody me={state.me} onMeUpdate={onMeUpdate} />;
  }
}

const REFILL_DATE_FMT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

function ProfileBody({
  me,
  onMeUpdate,
}: {
  me: Me;
  onMeUpdate: (me: Me) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <p className="email">{me.email}</p>
      {me.wallet && (
        <div className="plan-summary">
          <p>
            <Trans
              i18nKey="profile.plan"
              values={{ plan: me.wallet.plan_name }}
              components={[<strong key="0" />]}
            />
          </p>
          <p>
            <Trans
              i18nKey="profile.planTokensRemaining"
              values={{ count: me.wallet.plan_tokens }}
              components={[<strong key="0" />]}
            />
          </p>
          {me.wallet.package_tokens > 0 && (
            <p>
              <Trans
                i18nKey="profile.packageTokens"
                values={{ count: me.wallet.package_tokens }}
                components={[<strong key="0" />]}
              />
            </p>
          )}
          {me.wallet.next_refill_at && (
            <p className="plan-summary__refill">
              <Trans
                i18nKey="profile.nextRefill"
                values={{
                  date: REFILL_DATE_FMT.format(new Date(me.wallet.next_refill_at)),
                  count: me.wallet.next_refill_tokens,
                }}
                components={[<strong key="0" />, <strong key="1" />]}
              />
            </p>
          )}
        </div>
      )}
      <LanguagePicker me={me} onMeUpdate={onMeUpdate} />
      <button
        type="button"
        onClick={() => {
          void signOut();
        }}
      >
        {t('profile.signOut')}
      </button>
    </>
  );
}

function LanguagePicker({
  me,
  onMeUpdate,
}: {
  me: Me;
  onMeUpdate: (me: Me) => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  async function handleChange(value: string) {
    setSaving(true);
    try {
      const updated = await updateMe({ preferred_language: value });
      onMeUpdate(updated);
      const active = value || navigator.language.toLowerCase().split(/[-_]/, 1)[0];
      const target = (SUPPORTED_LANGUAGES as readonly string[]).includes(active)
        ? active
        : 'en';
      if (i18n.language !== target) await i18n.changeLanguage(target);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="language-picker">
      <label>
        <span>{t('profile.languageHeading')}</span>
        <select
          value={me.preferred_language}
          disabled={saving}
          onChange={(e) => void handleChange(e.target.value)}
        >
          <option value="">{t('profile.languageAuto')}</option>
          <option value="en">{t('profile.languageEnglish')}</option>
          <option value="he">{t('profile.languageHebrew')}</option>
        </select>
      </label>
    </div>
  );
}
